import React, { useMemo } from 'react';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { isTruthy } from '@chainflip/utils/guard';
import { abbreviate } from '@chainflip/utils/string';
import {
  AgeWithTooltip,
  CopyButton,
  Link,
  TokenWithChainByAsset,
  ViewEventButton,
} from '@/shared/components';
import Pill from '@/shared/components/flip-ui-kit/Pill';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import { useTableSort } from '@/shared/hooks';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { MinusCircleIcon, PlusCircleIcon } from '@/shared/icons/small';
import {
  TokenAmount,
  type TradingStrategy,
  chainflipAssetMap,
  formatUsdValue,
  COMPARATORS,
} from '@/shared/utils';
import { getStrategiesBalanceChanges } from '../../graphql/lp/strategies';

type StrategyHistoryEvent = {
  accountIdSs58: string;
  baseAsset: ChainflipAsset;
  quoteAsset: ChainflipAsset;
  baseAmount: bigint;
  quoteAmount: bigint;
  baseAssetAmountUsd: number;
  quoteAssetAmountUsd: number;
  type: 'ADD' | 'REMOVE';
  date: Date;
  blockId: number;
  indexInBlock: number;
};

const StrategyHistoryRow = ({
  displayAccountId,
  event: {
    accountIdSs58,
    baseAsset,
    quoteAsset,
    baseAmount,
    quoteAmount,
    baseAssetAmountUsd,
    quoteAssetAmountUsd,
    type,
    date,
    blockId,
    indexInBlock,
  },
}: {
  displayAccountId: boolean;
  event: StrategyHistoryEvent;
}) => {
  const baseAssetAmount = TokenAmount.fromAsset(baseAmount, baseAsset);
  const quoteAssetAmount = TokenAmount.fromAsset(quoteAmount, quoteAsset);

  const lpPath = `/lps/${accountIdSs58}`;
  const blockExplorerUrl = process.env.NEXT_PUBLIC_EXPLORER_URL;
  const lpUrl = blockExplorerUrl ? new URL(lpPath, blockExplorerUrl).toString() : lpPath;

  return (
    <tr className="text-12" key={`${blockId}-${indexInBlock}`}>
      <td>
        {type === 'ADD' ? (
          <Pill Icon={PlusCircleIcon} color="green" text="Added" />
        ) : (
          <Pill Icon={MinusCircleIcon} color="red" text="Removed" />
        )}
      </td>
      {displayAccountId && (
        <td className="font-aeonikMono">
          <div className="flex flex-row items-center gap-x-2 whitespace-nowrap">
            <Link
              href={lpUrl}
              underline
              target={blockExplorerUrl ? '_blank' : undefined}
              noPropagate
            >
              {abbreviate(accountIdSs58, 4)}
            </Link>
            <CopyButton textToCopy={accountIdSs58} />
          </div>
        </td>
      )}
      <td className="font-aeonikMono">
        <div className="flex flex-col gap-y-2 text-cf-white">
          <span className="flex gap-x-2">
            <TokenWithChainByAsset asset={baseAsset} /> {baseAssetAmount?.toFormatted()}{' '}
            {chainflipAssetMap[baseAsset].assetAndChainName}
          </span>
          <span className="flex gap-x-2">
            <TokenWithChainByAsset asset={quoteAsset} /> {quoteAssetAmount?.toFormatted()}{' '}
            {chainflipAssetMap[quoteAsset].assetAndChainName}
          </span>
        </div>
      </td>
      <td className="font-aeonikMono">
        <div className="flex flex-col text-cf-white">
          {formatUsdValue(baseAssetAmountUsd + quoteAssetAmountUsd)}
        </div>
      </td>
      <td>
        <ViewEventButton event={{ blockId, indexInBlock }} />
      </td>
      <td className="font-aeonikMono">
        <div className="flex flex-col text-cf-light-3">
          <AgeWithTooltip timestamp={date.toISOString()} />
        </div>
      </td>
    </tr>
  );
};

const SORT_FUNCTIONS: ((data: StrategyHistoryEvent) => number)[] = [
  () => 0,
  () => 0,
  () => 0,
  (data) => data.baseAssetAmountUsd + data.quoteAssetAmountUsd,
  () => 0,
  (data) => -data.date.getTime(),
];

export const StrategyHistoryTable = ({
  strategies,
  isLoading: propsIsLoading,
  displayAccountId = false,
}: {
  strategies?: TradingStrategy[];
  isLoading?: boolean;
  displayAccountId?: boolean;
}) => {
  const { setCol, sort } = useTableSort({
    initialSort: { col: 5, direction: 'ASC' as 'ASC' | 'DESC' },
  });

  const strategyIdSs58s = Array.from(
    new Set(strategies?.map((strategy) => strategy.strategyAccountSs58Id) ?? []),
  );

  const { data, isLoading } = useGqlQuery(getStrategiesBalanceChanges, {
    context: { clientName: 'lpProcessor' },
    variables: {
      idSs58s: strategyIdSs58s,
    },
    enabled: strategyIdSs58s.length > 0,
    refetchInterval: 60_000,
  });

  const unsortedData = useMemo(() => {
    const balanceChangeEvents =
      data?.account?.nodes.flatMap((account) => {
        const groupedBalanceChangeEvents = Object.groupBy(
          account.strategy.nodes[0]?.balanceChangeEvents.nodes,
          (balanceChangeEvent) =>
            `${balanceChangeEvent.event.block}-${balanceChangeEvent.event.indexInBlock}`,
        );

        return Object.values(groupedBalanceChangeEvents)
          .filter(isTruthy)
          .map((balanceChangeEvent): StrategyHistoryEvent => {
            const strategy = account.strategy.nodes[0];
            const amounts = balanceChangeEvent.reduce(
              (acc, { asset, amount, amountUsd }) => ({
                baseAmount: acc.baseAmount + (asset === strategy.baseAsset ? BigInt(amount) : 0n),
                quoteAmount:
                  acc.quoteAmount + (asset === strategy.quoteAsset ? BigInt(amount) : 0n),
                baseAssetAmountUsd:
                  acc.baseAssetAmountUsd + (asset === strategy.baseAsset ? Number(amountUsd) : 0),
                quoteAssetAmountUsd:
                  acc.quoteAssetAmountUsd + (asset === strategy.quoteAsset ? Number(amountUsd) : 0),
              }),
              { baseAmount: 0n, quoteAmount: 0n, baseAssetAmountUsd: 0, quoteAssetAmountUsd: 0 },
            );

            return {
              accountIdSs58: strategy.owner.idSs58,
              baseAsset: strategy.baseAsset,
              quoteAsset: strategy.quoteAsset,
              date: new Date(balanceChangeEvent[0].event.timestamp),
              blockId: balanceChangeEvent[0].event.block,
              indexInBlock: balanceChangeEvent[0].event.indexInBlock,
              type: balanceChangeEvent[0].type === 'FUNDS_ADDED' ? 'ADD' : 'REMOVE',
              ...amounts,
            };
          });
      }) ?? [];

    return balanceChangeEvents.filter(isTruthy);
  }, [data]);

  const sortedRows = useMemo(
    () =>
      unsortedData
        ?.toSorted((a, b) =>
          COMPARATORS[sort.direction](SORT_FUNCTIONS[sort.col](a), SORT_FUNCTIONS[sort.col](b)),
        )
        .map((node) => (
          <StrategyHistoryRow
            key={`${node.blockId}-${node.indexInBlock}`}
            displayAccountId={displayAccountId}
            event={node}
          />
        )),
    [unsortedData, sort],
  );

  return (
    <TableV2
      isLoading={isLoading || propsIsLoading}
      columns={[
        { name: 'Type', sortable: false },
        ...(displayAccountId ? [{ name: 'Account ID', sortable: false }] : []),
        { name: 'Assets / Amounts', sortable: false },
        { name: 'Value', sortable: true },
        { name: 'Event', sortable: false },
        { name: 'Age', sortable: true },
      ]}
      rows={sortedRows}
      sorting={{ setCol, sort }}
    />
  );
};
